/**
 * Shared Size Recommendation Panel
 * Used in: CustomerTryOnStudio.js (In-Store Customer), PublicTryOnStudio.js (In-Store Partner)
 *
 * Props:
 *   apiFetcher  - async (heightCm: number) => SizeRecommendResponse
 *                 Caller constructs the fetch; panel just passes heightCm and renders the result.
 *   productId   - string  (passed to onResult callback)
 *   garment     - product object (passed to onResult callback)
 *   onResult    - (productId, result, garment) => void  (optional)
 */
import React, { useState, useEffect } from 'react';

export const SIZE_PANEL_CSS = `
  @keyframes nkCzFadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes nkCzPop {
    0%   { transform: scale(0.4); opacity: 0; }
    65%  { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes nkCzRingFill {
    from { stroke-dashoffset: 276.5; }
  }
  @keyframes nkCzPulse {
    0%, 80%, 100% { opacity: 0.25; transform: scale(0.75); }
    40%           { opacity: 1;    transform: scale(1); }
  }
`;

const SZ_RING_R = 44;
const SZ_RING_C = 2 * Math.PI * SZ_RING_R;

export function getSizeZoneColor(pct) {
  if (pct < 45) return '#f97316';
  if (pct < 70) return '#eab308';
  return '#22c55e';
}

function parseHeightCm(cmInput, unit, feetVal, inchesVal) {
  if (unit === 'cm') {
    if (!cmInput || !String(cmInput).trim()) return null;
    const v = parseFloat(cmInput);
    return isNaN(v) ? null : Math.round(v);
  }
  const ft = parseFloat(feetVal);
  const inches = parseFloat(inchesVal) || 0;
  if (isNaN(ft)) return null;
  return Math.round((ft * 12 + inches) * 2.54);
}

const SizeBadgeRing = ({ size, percent }) => {
  const zoneColor = percent != null ? getSizeZoneColor(percent) : 'rgba(255,255,255,0.3)';
  const filled = percent != null ? (percent / 100) * SZ_RING_C : 0;
  const dashOffset = SZ_RING_C - filled;
  return (
    <div style={{ position: 'relative', width: 100, height: 100, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="100" height="100" viewBox="0 0 100 100" style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
        <circle cx="50" cy="50" r={SZ_RING_R} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="5" />
        {percent != null && (
          <circle cx="50" cy="50" r={SZ_RING_R} fill="none"
            stroke={zoneColor} strokeWidth="5" strokeLinecap="round"
            strokeDasharray={`${SZ_RING_C} 9999`} strokeDashoffset={dashOffset}
            style={{ animation: 'nkCzRingFill 1.1s cubic-bezier(0.22,1,0.36,1) forwards', filter: `drop-shadow(0 0 5px ${zoneColor}90)` }}
          />
        )}
      </svg>
      <div data-testid="size-badge" style={{
        width: 76, height: 76, borderRadius: '50%',
        background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'nkCzPop 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.2s both',
      }}>
        <span style={{ fontSize: size.length > 3 ? 20 : size.length > 2 ? 24 : 30, fontWeight: 900, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1 }}>
          {size}
        </span>
      </div>
    </div>
  );
};

const SizeResultDisplay = ({ result, onRefine }) => {
  const combinedNotes = React.useMemo(() => {
    if (result.photoSufficient) return result.fitNotes || null;
    const parts = [];
    if (result.insufficiencyMessage) parts.push(result.insufficiencyMessage);
    if (result.fitNotes) parts.push(result.fitNotes);
    return parts.length > 0 ? parts.join(' ') : null;
  }, [result]);

  const displayConfidence = React.useMemo(() => {
    const conf = result.confidencePercent ?? 0;
    if (!result.photoSufficient && conf === 0) return '<10';
    return conf.toString();
  }, [result]);

  const effectiveColor = React.useMemo(() => {
    const conf = result.confidencePercent ?? 0;
    if (!result.photoSufficient && conf === 0) return 5;
    return conf;
  }, [result]);

  return (
    <div data-testid="size-recommendation-result" style={{ marginTop: 12, borderRadius: 16, overflow: 'hidden', animation: 'nkCzFadeUp 0.4s cubic-bezier(0.22,1,0.36,1)' }}>
      <div style={{ background: 'linear-gradient(145deg, #0d1b3e 0%, #1a3070 45%, #1e40af 100%)', padding: '24px 20px 20px', position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse at 80% 20%, rgba(59,130,246,0.25) 0%, transparent 60%), radial-gradient(ellipse at 10% 80%, rgba(147,197,253,0.12) 0%, transparent 50%)' }} />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.07, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
        {!result.photoSufficient && (
          <div style={{ position: 'relative', marginBottom: 14, padding: '8px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span style={{ fontSize: 10, fontWeight: 600, color: '#fca5a5', letterSpacing: '0.02em' }}>Low confidence due to photo quality</span>
          </div>
        )}
        <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(147,197,253,0.6)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10, position: 'relative' }}>
          Recommended Size
        </div>
        <SizeBadgeRing size={result.recommendedSize} percent={effectiveColor} />
        {result.confidencePercent != null && (
          <div style={{ marginTop: 12, position: 'relative' }}>
            <div style={{ fontSize: 30, fontWeight: 900, lineHeight: 1, color: getSizeZoneColor(effectiveColor), letterSpacing: '-0.03em', textShadow: `0 0 20px ${getSizeZoneColor(effectiveColor)}60`, animation: 'nkCzFadeUp 0.4s 0.6s both' }}>
              {displayConfidence}%
            </div>
            <div style={{ marginTop: 4, fontSize: 9.5, fontWeight: 700, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              match confidence
            </div>
          </div>
        )}
      </div>
      {combinedNotes && (
        <div style={{ padding: '14px 16px', background: 'linear-gradient(to bottom, #f0f7ff, #f8fbff)', borderTop: '1px solid rgba(37,99,235,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
            <div style={{ width: 22, height: 22, borderRadius: 7, background: result.photoSufficient ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' : 'linear-gradient(135deg, #ef4444, #dc2626)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
              {result.photoSufficient
                ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              }
            </div>
            <p style={{ margin: 0, fontSize: 12, color: result.photoSufficient ? '#374151' : '#7f1d1d', lineHeight: 1.65 }}>{combinedNotes}</p>
          </div>
        </div>
      )}
      <div style={{ padding: '10px 16px', background: '#f8fbff', borderTop: '1px solid rgba(37,99,235,0.06)', display: 'flex', justifyContent: 'center' }}>
        <button onClick={onRefine} data-testid="size-refine-btn"
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#94a3b8', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 .49-5.18"/></svg>
          Try a different height
        </button>
      </div>
    </div>
  );
};

const SizePulsingDots = () => (
  <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
    {[0, 1, 2].map(i => (
      <span key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', animation: `nkCzPulse 1.2s ${i * 0.2}s ease-in-out infinite` }} />
    ))}
  </span>
);

const SizeRecommendPanel = ({ apiFetcher, productId, garment, onResult }) => {
  const [unit, setUnit] = useState('cm');
  const [heightInput, setHeightInput] = useState('');
  const [feetInput, setFeetInput] = useState('');
  const [inchesInput, setInchesInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => { if (result) setExpanded(true); }, [result]);

  const heightCm = parseHeightCm(heightInput, unit, feetInput, inchesInput);
  const isValidHeight = !!heightCm && heightCm >= 100 && heightCm <= 250;

  const handleUnitChange = (newUnit) => {
    setUnit(newUnit);
    setHeightInput('');
    setFeetInput('');
    setInchesInput('');
  };

  const handleRefine = () => {
    setResult(null);
    setError(null);
    setHeightInput('');
    setFeetInput('');
    setInchesInput('');
  };

  const handleGetSize = async () => {
    if (!isValidHeight || loading || !apiFetcher) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetcher(heightCm);
      const resultData = {
        recommendedSize: data.recommended_size,
        confidencePercent: data.confidence_percent,
        fitNotes: data.fit_notes,
        photoSufficient: data.photo_sufficient ?? true,
        insufficiencyMessage: data.insufficiency_message,
        engine: data.engine,
        cached: data.cached,
        creditsConsumed: data.credits_consumed
      };
      setResult(resultData);
      if (onResult) onResult(productId, resultData, garment);
    } catch (err) {
      setError(err.message || 'Failed to get size recommendation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{SIZE_PANEL_CSS}</style>
      <div data-testid="size-recommend-panel" style={{ borderRadius: 20, overflow: 'hidden', background: '#fff', border: '1px solid rgba(37,99,235,0.15)', boxShadow: '0 2px 20px rgba(37,99,235,0.08), inset 0 1px 0 rgba(255,255,255,0.9)' }}>
        <button data-testid="size-recommend-toggle" onClick={() => setExpanded(v => !v)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 15px', background: expanded ? 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)' : '#fff', border: 'none', borderBottom: expanded ? '1px solid rgba(37,99,235,0.1)' : 'none', cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #3b82f6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 3px 10px rgba(37,99,235,0.4)', flexShrink: 0 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
              </svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>Find Your Perfect Size</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                {result
                  ? <><span>Recommended: </span><strong style={{ color: result.photoSufficient ? '#2563eb' : '#ef4444' }}>{result.recommendedSize}</strong>{!result.photoSufficient && <span style={{ color: '#ef4444' }}> (low confidence)</span>}</>
                  : 'AI analyzes your photo to find the right fit'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {result && !expanded && (
              <div style={{ background: result.photoSufficient ? 'linear-gradient(135deg, #1d4ed8, #2563eb)' : 'linear-gradient(135deg, #dc2626, #ef4444)', color: '#fff', fontSize: 12, fontWeight: 800, padding: '3px 11px', borderRadius: 99, letterSpacing: '0.02em' }}>
                {result.recommendedSize}
              </div>
            )}
            <div style={{ width: 28, height: 28, borderRadius: 8, background: expanded ? '#dbeafe' : '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>
        </button>

        {expanded && (
          <div style={{ padding: '16px 15px 15px' }}>
            {result ? (
              <SizeResultDisplay result={result} onRefine={handleRefine} />
            ) : (
              <div>
                <div style={{ marginBottom: 14, textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Enter your height to find your size</p>
                  <p style={{ margin: '3px 0 0', fontSize: 11, color: '#94a3b8' }}>AI analyzes your photo to find the right fit</p>
                </div>
                <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 12, padding: 3, marginBottom: 12, gap: 2 }}>
                  {['cm', 'ft'].map(u => (
                    <button key={u} data-testid={`size-unit-${u}`} onClick={() => handleUnitChange(u)}
                      style={{ flex: 1, padding: '7px 10px', borderRadius: 9, border: 'none', cursor: 'pointer', background: unit === u ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' : 'transparent', color: unit === u ? '#fff' : '#64748b', fontSize: 12, fontWeight: unit === u ? 700 : 500, transition: 'all 0.2s' }}>
                      {u === 'ft' ? 'ft / in' : 'cm'}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                  {unit === 'cm' ? (
                    <div style={{ flex: 1, background: '#f8faff', border: '1.5px solid #dbeafe', borderRadius: 13, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                      <input data-testid="size-height-input" type="text" inputMode="decimal" value={heightInput} onChange={e => setHeightInput(e.target.value)} placeholder="e.g. 165"
                        style={{ flex: 1, padding: '11px 12px', border: 'none', background: 'transparent', fontSize: 17, fontWeight: 700, color: '#1e293b', outline: 'none' }} />
                      <span style={{ paddingRight: 13, fontSize: 10, fontWeight: 800, color: '#3b82f6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>cm</span>
                    </div>
                  ) : (
                    <div style={{ flex: 1, display: 'flex', gap: 6 }}>
                      <div style={{ flex: 1, background: '#f8faff', border: '1.5px solid #dbeafe', borderRadius: 13, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                        <input data-testid="size-height-ft-input" type="text" inputMode="numeric" value={feetInput} onChange={e => setFeetInput(e.target.value)} placeholder="5"
                          style={{ flex: 1, padding: '11px 10px', border: 'none', background: 'transparent', fontSize: 17, fontWeight: 700, color: '#1e293b', outline: 'none', textAlign: 'center' }} />
                        <span style={{ paddingRight: 10, fontSize: 10, fontWeight: 800, color: '#3b82f6', textTransform: 'uppercase' }}>ft</span>
                      </div>
                      <div style={{ flex: 1, background: '#f8faff', border: '1.5px solid #dbeafe', borderRadius: 13, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                        <input data-testid="size-inches-input" type="text" inputMode="numeric" value={inchesInput} onChange={e => setInchesInput(e.target.value)} placeholder="7"
                          style={{ flex: 1, padding: '11px 10px', border: 'none', background: 'transparent', fontSize: 17, fontWeight: 700, color: '#1e293b', outline: 'none', textAlign: 'center' }} />
                        <span style={{ paddingRight: 10, fontSize: 10, fontWeight: 800, color: '#3b82f6', textTransform: 'uppercase' }}>in</span>
                      </div>
                    </div>
                  )}
                  <button data-testid="size-recommend-submit" onClick={handleGetSize} disabled={!isValidHeight || loading}
                    style={{ padding: '0 16px', minHeight: 46, minWidth: 46, background: isValidHeight && !loading ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : '#e2e8f0', color: isValidHeight && !loading ? '#fff' : '#94a3b8', border: 'none', borderRadius: 13, fontSize: 13, fontWeight: 700, cursor: isValidHeight && !loading ? 'pointer' : 'default', boxShadow: isValidHeight && !loading ? '0 4px 14px rgba(37,99,235,0.35)' : 'none', transition: 'all 0.2s', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 7 }}>
                    {loading ? <><SizePulsingDots /><span>Analyzing</span></> : <><span>Get Size</span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg></>}
                  </button>
                </div>
                {unit === 'ft' && (feetInput || inchesInput) && heightCm && (
                  <div style={{ marginTop: 5, fontSize: 11, color: '#94a3b8' }}>≈ {heightCm} cm</div>
                )}
                {error && (
                  <div data-testid="size-recommend-error" style={{ marginTop: 10, padding: '10px 13px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 11, fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {error}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SizeRecommendPanel;
